import { useEffect, useId, useRef, useState } from 'react';
import { PermissionAwareInput } from '../../../contexts/PermissionAccessContext';
import safetySystemService from '../../../services/safetySystem.service';
import type { SafetyLookupOption } from '../../../types/safetySystem';
import { Field, inputClass } from './unitFields';

/**
 * Driver typeahead for the Safety Tables drawer over GET /tables/lookups/drivers
 * (value = driver recno, label = "First Last (unit)"). A combobox: the server call is
 * debounced and cancelled when stale; ArrowUp / ArrowDown move the active option,
 * Enter picks it, Escape closes the list; clearing the text clears the value.
 */

const DEBOUNCE_MS = 200;
const BLUR_CLOSE_MS = 150;

export interface DriverPickerProps {
  label: string;
  value: unknown;
  /** The stored `<name>_label` for the current value. */
  labelText: string;
  onPick: (value: string | number, label: string) => void;
  readOnly: boolean;
}

export default function DriverPicker({ label, value, labelText, onPick, readOnly }: DriverPickerProps) {
  const id = useId();
  const listId = `${id}-listbox`;
  const optionId = (i: number) => `${id}-option-${i}`;
  const [text, setText] = useState(labelText);
  const [query, setQuery] = useState<string | null>(null);
  const [hits, setHits] = useState<SafetyLookupOption[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => { setText(labelText); }, [labelText, value]);
  useEffect(() => () => { if (blurTimer.current) clearTimeout(blurTimer.current); }, []);

  useEffect(() => {
    if (query === null) return undefined;
    const q = query.trim();
    if (!q) { setHits([]); setOpen(false); setActive(-1); return undefined; }
    const controller = new AbortController();
    const timer = setTimeout(() => {
      safetySystemService.driverLookup(q, controller.signal)
        .then((rows) => { if (!controller.signal.aborted) { setHits(rows ?? []); setOpen(true); setActive(rows?.length ? 0 : -1); } })
        .catch(() => { if (!controller.signal.aborted) setHits([]); });
    }, DEBOUNCE_MS);
    return () => { controller.abort(); clearTimeout(timer); };
  }, [query]);

  const close = () => { setOpen(false); setActive(-1); };
  const pick = (hit: SafetyLookupOption) => { close(); setHits([]); setQuery(null); setText(hit.label); onPick(hit.value, hit.label); };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') { if (open) { e.preventDefault(); close(); } return; }
    if (!hits.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive((a) => (a + 1) % hits.length); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setOpen(true); setActive((a) => (a <= 0 ? hits.length - 1 : a - 1)); return; }
    if (e.key === 'Enter' && open && active >= 0) { e.preventDefault(); pick(hits[active]); }
  };

  const expanded = open && hits.length > 0;
  return (
    <Field label={label}>
      <div className="relative">
        <PermissionAwareInput
          type="text"
          role="combobox"
          aria-label={label}
          aria-autocomplete="list"
          aria-expanded={expanded}
          aria-controls={listId}
          aria-activedescendant={expanded && active >= 0 ? optionId(active) : undefined}
          autoComplete="off"
          className={inputClass}
          value={text}
          disabled={readOnly}
          placeholder="Type a driver name or unit…"
          onChange={(e) => { setText(e.target.value); setQuery(e.target.value); if (!e.target.value) onPick('', ''); }}
          onKeyDown={onKeyDown}
          onBlur={() => { blurTimer.current = setTimeout(close, BLUR_CLOSE_MS); }}
          onFocus={() => { if (hits.length) { setOpen(true); setActive(0); } }}
        />
        {expanded && (
          <ul id={listId} role="listbox" aria-label={`${label} suggestions`}
            className="absolute left-0 z-20 mt-0.5 max-h-56 w-full min-w-[16rem] overflow-y-auto rounded-md border border-rule bg-panel py-1 text-sm shadow-lg">
            {hits.map((hit, i) => (
              <li key={String(hit.value)} id={optionId(i)} role="option" aria-selected={i === active} tabIndex={-1}
                onMouseDown={(e) => { e.preventDefault(); pick(hit); }} onClick={() => pick(hit)} onMouseEnter={() => setActive(i)}
                className={`cursor-pointer px-2 py-1 hover:bg-highlight ${i === active ? 'bg-highlight' : ''}`}>
                {hit.label}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Field>
  );
}
